import { useMemo } from 'react';

export interface TrailPoint {
  frame: number;
  x: number;       // 0-1 normalized video coords
  y: number;       // 0-1 normalized video coords
  velocity: number; // units/second in normalized coord space
}

/**
 * Minimum detector confidence for a landmark to be drawn in a trail.
 *
 * 0.5 matches the backend's established gate for landmark-trusting features
 * (axis_goal_evaluation.py visibility_threshold=0.5, barycentric.py 0.5).
 * The previous 0.05 floor only rejected "essentially missing" detections and
 * let confidently-wrong ones through: on Felix_Lunge frame 45 the tracked
 * right "ankle" had migrated 69% of the way up the shin toward the knee at
 * confidence 0.054 — 0.004 above the old floor — and the trail drew the
 * planted trailing foot launching into the air (owner report 2026-08-02).
 *
 * Provenance (22-clip / 13,336-limb-sample corpus study, 2026-08-02): healthy
 * limb keypoints sit at p5=0.747 / p10=0.838; a 0.5 floor drops 2.74% of
 * samples — the low-trust tail where intra-limb swaps live — and there is a
 * wide empty band between ~0.45 and ~0.75, so the cut sits in a natural gap
 * rather than slicing through real data.  For a trail (a drawing of where a
 * joint went) omitting an uncertain point is strictly better than drawing a
 * wrong one: the arc-length walk interpolates smoothly across gaps.
 */
const MIN_TRAIL_VISIBILITY: number = 0.5;

/**
 * Physiological ceiling on per-frame landmark travel, in normalized [0,1]
 * coords per FRAME OF ACTUAL GAP (scaled by the gap to the last kept frame,
 * so recovery after a dropout is not over-rejected).
 *
 * The previous flat 0.25 guard was calibrated for detector swaps to a
 * *background subject* (a cross-frame teleport); an intra-limb swap
 * (ankle→knee) travels only ~0.10–0.18 and sailed under it.
 *
 * Provenance (same corpus study): real inter-frame hops are p99=0.0405,
 * p99.5=0.0516, and the guard's own long-standing rationale ("real fast human
 * motion peaks around 5-8% per frame at 30 fps") is corroborated by the data.
 * 0.09 sits above every observed legitimate hop (p99.9 of the corpus is
 * inflated by the swap events themselves) while rejecting the known
 * intra-limb swap hops (0.10–0.13).  Verified by replaying both gates over
 * the corpus: 6/7 known swap events rejected, 2 additional velocity
 * rejections were both detector glitches (one was the contralateral ankle at
 * a known swap frame), total rejection 2.75% of samples.
 */
const MAX_LANDMARK_JUMP_PER_FRAME: number = 0.09;

/**
 * Pure Pass 1-3: confidence/velocity-gated trail extraction + velocity
 * normalization, with NO smoothing and NO memoization. This is the exact
 * body previously inlined in `useTrajectoryTrail`'s `useMemo` callback —
 * extracted verbatim (no logic changes) so it can be:
 *  - called from the hook below (unchanged behaviour), and
 *  - imported directly by non-React callers (e.g. the ribbon owner-preview
 *    builder, frontend/scripts/build-ribbon-preview.mjs) that need the SAME
 *    gated trail points the live app feeds into the ribbon/dots renderers,
 *    without standing up a React render tree.
 *
 * See `useTrajectoryTrail` below for the parameter contract.
 */
export function computeTrailPointsFromPoseData(
  poseData: Array<{ landmarks: Array<{ x: number; y: number; visibility?: number }> }> | null,
  landmarkIndex: number,
  fps: number,
): { trailPoints: TrailPoint[]; maxVelocity: number } {
  if (!Array.isArray(poseData) || poseData.length === 0 || fps <= 0) {
    return { trailPoints: [], maxVelocity: 0 };
  }

  // --- Pass 1: collect valid (x, y) positions per frame, rejecting both
  //     low-confidence detections and physiologically impossible jumps
  //     (intra-limb swaps like ankle→knee, and inter-person swaps).  Each
  //     candidate is compared against the last KEPT point so that a single
  //     bad frame doesn't poison the rest of the sequence; the jump
  //     allowance scales with the actual frame gap so recovery after a
  //     dropout is not over-rejected. ---
  type RawPoint = { frame: number; x: number; y: number } | null;
  const raw: RawPoint[] = [];
  let lastKept: { frame: number; x: number; y: number } | null = null;

  for (let frameIdx = 0; frameIdx < poseData.length; frameIdx++) {
    const frameData = poseData[frameIdx];
    const lm = frameData?.landmarks?.[landmarkIndex];

    if (!lm) {
      raw.push(null);
      continue;
    }
    // Confidence gate: below MIN_TRAIL_VISIBILITY the detector is guessing
    // (motion blur / occlusion) — omit rather than draw a wrong point.
    if (lm.visibility !== undefined && lm.visibility < MIN_TRAIL_VISIBILITY) {
      raw.push(null);
      continue;
    }

    // Physiological velocity gate: reject jumps beyond what a human joint
    // can travel in the elapsed frames.  Catches the residual swaps that
    // pass the confidence gate (a mis-identified joint teleports; a real
    // joint doesn't).
    if (lastKept !== null) {
      const frameGap = frameIdx - lastKept.frame;
      const dx = lm.x - lastKept.x;
      const dy = lm.y - lastKept.y;
      if (Math.hypot(dx, dy) > MAX_LANDMARK_JUMP_PER_FRAME * frameGap) {
        raw.push(null);
        continue;
      }
    }

    const point = { frame: frameIdx, x: lm.x, y: lm.y };
    raw.push(point);
    lastKept = point;
  }

  // --- Pass 2: compute velocity via centered finite differences where
  //     possible, forward/backward difference at the boundaries ---
  const trailPoints: TrailPoint[] = [];

  for (let i = 0; i < raw.length; i++) {
    const cur = raw[i];
    if (!cur) continue;

    let velocity = 0;

    // Look for the nearest valid neighbours for the difference
    let prev: RawPoint = null;
    let next: RawPoint = null;

    // Search backward
    for (let j = i - 1; j >= 0 && !prev; j--) {
      if (raw[j]) prev = raw[j];
    }
    // Search forward
    for (let j = i + 1; j < raw.length && !next; j++) {
      if (raw[j]) next = raw[j];
    }

    if (prev && next) {
      // Centered difference over the actual frame gap (handles sparse data)
      const dt = (next.frame - prev.frame) / fps;
      const dx = next.x - prev.x;
      const dy = next.y - prev.y;
      velocity = Math.hypot(dx, dy) / dt;
    } else if (prev) {
      // Backward difference (last valid point)
      const dt = (cur.frame - prev.frame) / fps;
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      velocity = Math.hypot(dx, dy) / dt;
    } else if (next) {
      // Forward difference (first valid point)
      const dt = (next.frame - cur.frame) / fps;
      const dx = next.x - cur.x;
      const dy = next.y - cur.y;
      velocity = Math.hypot(dx, dy) / dt;
    }

    trailPoints.push({ frame: cur.frame, x: cur.x, y: cur.y, velocity });
  }

  // --- Pass 3: compute maxVelocity for normalization ---
  let maxVelocity = 0;
  for (const pt of trailPoints) {
    if (pt.velocity > maxVelocity) maxVelocity = pt.velocity;
  }

  // Guard: if everything is static (e.g. single frame), avoid divide-by-zero
  // downstream by returning a small positive floor.
  if (maxVelocity === 0) maxVelocity = 1;

  return { trailPoints, maxVelocity };
}

/**
 * Extracts per-frame position for a landmark and computes velocity via finite
 * differences.  All computation is memoized — the result only recomputes when
 * poseData, landmarkIndex, or fps change.
 *
 * Velocity is expressed in normalized-coordinate units per second so that it
 * remains resolution-independent.  Callers typically multiply by canvas
 * dimensions for display purposes, but the stored value is kept unit-agnostic
 * to support both template and athlete coordinate spaces.
 *
 * @param poseData      Array of per-frame pose objects from the API.
 * @param landmarkIndex COCO / MediaPipe landmark index to track.
 * @param fps           Playback frame rate used to convert frame deltas to time.
 */
function useTrajectoryTrail(
  poseData: Array<{ landmarks: Array<{ x: number; y: number; visibility?: number }> }> | null,
  landmarkIndex: number,
  fps: number,
): { trailPoints: TrailPoint[]; maxVelocity: number } {
  // Pass 1-3: gated trail extraction + velocity normalization. Note this does
  // NOT early-return on an empty `trailPoints` (e.g. every frame gated out) —
  // it still runs the maxVelocity floor-to-1 step, matching the original
  // inlined behaviour exactly. No render-time smoothing: both trail styles
  // consume the same gated points (Photon Trace handoff, INTEGRATION.md).
  return useMemo(
    () => computeTrailPointsFromPoseData(poseData, landmarkIndex, fps),
    [poseData, landmarkIndex, fps],
  );
}

export default useTrajectoryTrail;
